/**
 * Free-gift cart reconciler + atomic bundle mutation.
 *
 * Free-gift lines ("individual") are grouped to their hero subscription line(s) ("main") by the
 * `_bundle_id` property (= hero product id):
 *   - gift quantity is capped at the sum of the main lines' quantities in its bundle group — a
 *     ceiling, not a forced equality, so a customer's manual decrease sticks rather than reverting
 *   - gift removed  = when its bundle group has no main line left
 *
 * IMPORTANT — Recharge enforces bundle integrity: a request that would leave a subscription
 * "individual" with no "main" is rejected (422 "Order cannot be placed at this time"). The theme
 * changes lines by *index* via /cart/change, so removing/reducing a hero on its own passes through
 * that invalid orphan state and fails. We therefore intercept the theme's index-based
 * `CartItems.updateQuantity` and, when the affected line is a hero ("main"), mutate the hero AND
 * its gift siblings TOGETHER in one key-based /cart/update — the cart is always valid, never
 * orphaned.
 *
 * A passive reconcile pass (subscribed to cartUpdate) remains as a safety net for external/edge
 * cart edits. Both are idempotent and re-entrancy guarded.
 */
(function () {
  if (window.__cartGiftReconcilerLoaded) return;
  window.__cartGiftReconcilerLoaded = true;

  let reconciling = false;
  let lastCart = null;

  const routes = () => window.FoxTheme.routes;
  const pubsub = () => window.FoxTheme.pubsub;

  // ---- helpers ----------------------------------------------------------------

  // POST a key-keyed quantity update; returns the updated cart JSON.
  function postUpdate(updates) {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    return fetch(`${routes().cart_update_url}`, {
      ...window.FoxTheme.utils.fetchConfig(),
      body: JSON.stringify({ updates, sections, sections_url: window.location.pathname }),
    }).then((response) => response.json());
  }

  function publishCart(cart, extra) {
    pubsub().publish(pubsub().PUB_SUB_EVENTS.cartUpdate, Object.assign({ cart }, extra || {}));
  }

  // Build the key→quantity map for changing one hero ("main") line. A gift still tracking the
  // hero exactly (never manually reduced) keeps scaling with it, up or down — that's the default,
  // expected behavior. A gift already below the PRIOR hero total was deliberately reduced by the
  // customer, and that choice sticks: it's only ever clamped down further if the new (smaller)
  // ceiling would now be exceeded, never bumped back up.
  function bundleUpdatesForMainChange(items, changedMain, newMainQty) {
    const bundleId = changedMain.properties._bundle_id;

    let otherMainQty = 0;
    items.forEach((item) => {
      const p = item.properties || {};
      if (p._bundle_item === 'main' && p._bundle_id === bundleId && item.key !== changedMain.key) {
        otherMainQty += item.quantity;
      }
    });
    const previousCeiling = otherMainQty + changedMain.quantity; // hero total before this change
    const newCeiling = otherMainQty + newMainQty; // hero total after this change; 0 removes gifts

    const updates = {};
    updates[changedMain.key] = newMainQty;
    items.forEach((item) => {
      const p = item.properties || {};
      if (p._bundle_item !== 'individual' || p._bundle_id !== bundleId) return;
      if (item.quantity === previousCeiling || item.quantity > newCeiling) {
        updates[item.key] = newCeiling;
      }
    });
    return updates;
  }

  // ---- atomic bundle mutation (patches the theme's index-based updateQuantity) -------------

  function patchCartItems() {
    const CartItems = window.customElements && customElements.get('cart-items');
    if (!CartItems || CartItems.prototype.__giftBundlePatched) return;
    CartItems.prototype.__giftBundlePatched = true;

    const original = CartItems.prototype.updateQuantity;

    CartItems.prototype.updateQuantity = function (line, quantity, name, target) {
      const run = () => {
        const items = (lastCart && lastCart.items) || [];
        const changed = items[Number(line) - 1]; // `line` is the 1-based cart index
        if (!changed) return original.call(this, line, quantity, name, target);
        const props = changed.properties || {};
        const newQty = Number(quantity);

        const isHero = props._bundle_item === 'main' && props._bundle_id != null && props._bundle_id !== '';

        // Base keyed updates for this change: a gift-bundle hero syncs its gift siblings;
        // anything else is just the changed line.
        const updates = isHero
          ? bundleUpdatesForMainChange(items, changed, newQty)
          : { [changed.key]: newQty };

        // Fold in any paid add-on ("DEAL") this change would orphan, so the hero, its gifts AND
        // its deal add-ons all leave in ONE keyed /cart/update — the deal disappears in the same
        // request as the free gift, with no lagging second update. cart-addon-reconciler.js's
        // passive pass stays as the safety net for edits that don't route through here.
        const addonApi = window.CartAddonReconciler;
        const addonUpdates = addonApi ? addonApi.orphanAddonUpdates(items, updates) : {};
        Object.assign(updates, addonUpdates);

        // Nothing special (plain single-line change, no gifts, no orphaned add-ons) → let the
        // theme handle it natively so its own quantity UX/animation is preserved.
        if (!isHero && Object.keys(addonUpdates).length === 0) {
          return original.call(this, line, quantity, name, target);
        }

        // Mutate hero + its gifts (+ orphaned deals) together so the cart is never left invalid.
        this.showLoader(line);
        reconciling = true;
        postUpdate(updates)
          .then((cart) => publishCart(cart, { sources: 'cart-items', target, line, name }))
          .catch((error) => console.error('[free-gift] bundle update failed', error))
          .finally(() => {
            reconciling = false;
          });
      };

      if (lastCart) return run.call(this);
      // Cold start (no cart cached yet): fetch once, then decide.
      fetch(`${routes().cart_url}`, { ...window.FoxTheme.utils.fetchConfig('json', 'GET') })
        .then((r) => r.json())
        .then((cart) => {
          lastCart = cart;
          run.call(this);
        })
        .catch(() => original.call(this, line, quantity, name, target));
    };
  }

  // ---- passive safety-net reconcile (external/edge edits) ----------------------------------

  function reconcile(payload) {
    if (payload && payload.cart) lastCart = payload.cart;
    if (reconciling) return;

    const items = (lastCart && lastCart.items) || [];
    const mainQtyByBundle = {};
    const giftLines = [];
    items.forEach((item) => {
      const p = item.properties || {};
      if (!p._bundle_item || p._bundle_id == null || p._bundle_id === '') return;
      if (p._bundle_item === 'main') {
        mainQtyByBundle[p._bundle_id] = (mainQtyByBundle[p._bundle_id] || 0) + item.quantity;
      } else if (p._bundle_item === 'individual') {
        giftLines.push({ key: item.key, quantity: item.quantity, bundleId: p._bundle_id });
      }
    });

    const updates = {};
    giftLines.forEach((gift) => {
      // Ceiling only: clamp down if the hero total shrank below what's in the cart (or the hero
      // is gone entirely), but never push a gift back up just because it's below the ceiling —
      // that's a customer's own deliberate decrease, and it must stick.
      const ceiling = mainQtyByBundle[gift.bundleId] || 0;
      if (gift.quantity > ceiling) updates[gift.key] = ceiling;
    });
    if (Object.keys(updates).length === 0) return;

    reconciling = true;
    postUpdate(updates)
      .then((cart) => publishCart(cart))
      .catch((error) => console.error('[free-gift] reconcile failed', error))
      .finally(() => {
        reconciling = false;
      });
  }

  function seedCart() {
    fetch(`${routes().cart_url}`, { ...window.FoxTheme.utils.fetchConfig('json', 'GET') })
      .then((r) => r.json())
      .then((cart) => {
        lastCart = cart;
      })
      .catch(() => {});
  }

  function init() {
    if (!window.FoxTheme || !window.FoxTheme.pubsub) return false;
    pubsub().subscribe(pubsub().PUB_SUB_EVENTS.cartUpdate, reconcile);
    patchCartItems();
    seedCart();
    return true;
  }

  if (!init()) {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
